import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    username: '',
    email: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [transactions, setTransactions] = useState([]);
  const [watchHistory, setWatchHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setProfileData({
        username: user.username || '',
        email: user.email || ''
      });
    }
    fetchTransactions();
    fetchWatchHistory();
  }, [user]);

  const fetchTransactions = async () => {
    try {
      const res = await axios.get('/api/payments/transactions');
      setTransactions(res.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const fetchWatchHistory = async () => {
    try {
      const res = await axios.get('/api/users/history');
      setWatchHistory(res.data);
    } catch (error) {
      console.error('Error fetching watch history:', error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const res = await axios.put('/api/users/profile', profileData);
      updateUser(res.data.user);
      setMessage('Profil mis à jour avec succès');
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      await axios.put('/api/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setMessage('Mot de passe modifié avec succès');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de la modification du mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#141414]">
      <Header />
      
      <div className="container mx-auto px-4 py-8 mt-8 md:mt-0">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-6 md:mb-8">Mon profil</h1>

        {/* Tabs */}
        <div className="flex overflow-x-auto space-x-2 md:space-x-4 mb-6 md:mb-8 border-b border-gray-800 scrollbar-hide">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-3 md:px-4 py-2 font-semibold text-sm md:text-base whitespace-nowrap ${
              activeTab === 'profile'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Profil
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`px-3 md:px-4 py-2 font-semibold text-sm md:text-base whitespace-nowrap ${
              activeTab === 'password'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Mot de passe
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`px-3 md:px-4 py-2 font-semibold text-sm md:text-base whitespace-nowrap ${
              activeTab === 'transactions'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Transactions
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-3 md:px-4 py-2 font-semibold text-sm md:text-base whitespace-nowrap ${
              activeTab === 'history'
                ? 'text-white border-b-2 border-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Historique
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-[#1A1A1A] rounded-lg p-4 md:p-6 max-w-2xl">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">Informations du profil</h2>
            
            {message && (
              <div className="mb-4 p-3 bg-green-900/50 border border-green-700 rounded text-green-200 text-sm">
                {message}
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded text-red-200 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label className="block text-white mb-2 text-sm md:text-base">Nom d'utilisateur</label>
                <input
                  type="text"
                  value={profileData.username}
                  onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                  className="w-full px-3 md:px-4 py-2 md:py-3 bg-transparent text-white rounded-lg border border-[#FFFFFF] focus:border-white focus:outline-none text-sm md:text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-white mb-2 text-sm md:text-base">Email</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  className="w-full px-3 md:px-4 py-2 md:py-3 bg-transparent text-white rounded-lg border border-[#FFFFFF] focus:border-white focus:outline-none text-sm md:text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-white mb-2 text-sm md:text-base">Statut de l'abonnement</label>
                <div className="p-3 md:p-4 bg-transparent rounded-lg">
                  {user?.subscription?.isActive && new Date(user.subscription.endDate) > new Date() ? (
                    <div>
                      <p className="text-green-400 font-semibold text-sm md:text-base">Actif</p>
                      <p className="text-gray-300 text-xs md:text-sm">
                        Plan : {user.subscription.plan} | Expire le : {formatDate(user.subscription.endDate)}
                      </p>
                    </div>
                  ) : (
                    <p className="text-red-400 text-sm md:text-base">Aucun abonnement actif</p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full md:w-auto px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold disabled:opacity-50 text-sm md:text-base"
              >
                {loading ? 'Mise à jour...' : 'Mettre à jour le profil'}
              </button>
            </form>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="bg-[#1A1A1A] rounded-lg p-4 md:p-6 max-w-2xl">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">Changer le mot de passe</h2>
            
            {message && (
              <div className="mb-4 p-3 bg-green-900/50 border border-green-700 rounded text-green-200 text-sm">
                {message}
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded text-red-200 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-white mb-2 text-sm md:text-base">Mot de passe actuel</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full px-3 md:px-4 py-2 md:py-3 bg-transparent text-white rounded-lg border border-[#FFFFFF] focus:border-white focus:outline-none text-sm md:text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-white mb-2 text-sm md:text-base">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-3 md:px-4 py-2 md:py-3 bg-transparent text-white rounded-lg border border-[#FFFFFF] focus:border-white focus:outline-none text-sm md:text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-white mb-2 text-sm md:text-base">Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-3 md:px-4 py-2 md:py-3 bg-transparent text-white rounded-lg border border-[#FFFFFF] focus:border-white focus:outline-none text-sm md:text-base"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full md:w-auto px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold disabled:opacity-50 text-sm md:text-base"
              >
                {loading ? 'Modification...' : 'Changer le mot de passe'}
              </button>
            </form>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="bg-[#1A1A1A] rounded-lg p-4 md:p-6">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">Historique des transactions</h2>
            
            {transactions.length === 0 ? (
              <p className="text-gray-400 text-sm md:text-base">Aucune transaction trouvée</p>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {transactions.map((transaction) => (
                    <div key={transaction._id} className="bg-[#262626] rounded-lg p-4 space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-white font-semibold text-sm">{transaction.plan}</p>
                          <p className="text-gray-400 text-xs">{formatDate(transaction.createdAt)}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          transaction.status === 'completed' ? 'bg-green-900/50 text-green-200' :
                          transaction.status === 'pending' ? 'bg-yellow-900/50 text-yellow-200' :
                          'bg-red-900/50 text-red-200'
                        }`}>
                          {transaction.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-gray-300 text-sm">{transaction.amount} {transaction.currency}</p>
                        <p className="text-gray-400 text-xs capitalize">{transaction.paymentMethod}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="pb-4 text-white">Date</th>
                        <th className="pb-4 text-white">Plan</th>
                        <th className="pb-4 text-white">Montant</th>
                        <th className="pb-4 text-white">Mode de paiement</th>
                        <th className="pb-4 text-white">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((transaction) => (
                        <tr key={transaction._id} className="border-b border-gray-700">
                          <td className="py-4 text-gray-300">{formatDate(transaction.createdAt)}</td>
                          <td className="py-4 text-gray-300">{transaction.plan}</td>
                          <td className="py-4 text-gray-300">{transaction.amount} {transaction.currency}</td>
                          <td className="py-4 text-gray-300 capitalize">{transaction.paymentMethod}</td>
                          <td className="py-4">
                            <span className={`px-3 py-1 rounded text-sm ${
                              transaction.status === 'completed' ? 'bg-green-900/50 text-green-200' :
                              transaction.status === 'pending' ? 'bg-yellow-900/50 text-yellow-200' :
                              'bg-red-900/50 text-red-200'
                            }`}>
                              {transaction.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Watch History Tab */}
        {activeTab === 'history' && (
          <div className="bg-[#1A1A1A] rounded-lg p-4 md:p-6">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">Historique de visionnage</h2>
            
            {watchHistory.length === 0 ? (
              <p className="text-gray-400 text-sm md:text-base">Aucun historique de visionnage trouvé</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {watchHistory.map((item) => {
                  const isMovie = !!item.movieId;
                  const isSeries = !!item.seriesId;
                  const content = item.movieId || item.seriesId;
                  const link = isMovie 
                    ? `/movie/${content?._id || content}` 
                    : `/series/${content?._id || content}`;
                  
                  return (
                    <a
                      key={item._id}
                      href={link}
                      className="group block"
                    >
                      <div className="rounded-3xl bg-[#262626] border border-white/5 p-4 transition hover:border-white/20">
                        <div className="relative aspect-[2/3] rounded-2xl overflow-hidden bg-black/40">
                          {content?.poster ? (
                            <img
                              src={content.poster}
                              alt={content.title}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                              No Image
                            </div>
                          )}

                          {/* Badge for Series */}
                          {isSeries && (
                            <div className="absolute top-2 right-2 px-2 py-1 bg-blue-600 text-white text-xs rounded">
                              S{item.seasonNumber}E{item.episodeNumber}
                            </div>
                          )}
                        </div>

                        <h3 className="mt-4 text-sm font-semibold text-white line-clamp-1">
                          {content?.title || (isMovie ? 'Movie' : 'Series')}
                        </h3>

                        <p className="mt-2 text-xs text-gray-400">
                          Regardé le {formatDate(item.watchedAt)}
                        </p>

                        <div className="mt-4 flex items-center justify-between">
                          <span className="px-3 py-2 rounded-full bg-green-500/20 border border-green-500/30 text-xs text-green-400">
                            ✓ Regardé
                          </span>

                          <svg width="24" height="24" viewBox="0 0 30 30" fill="none" className="opacity-60 group-hover:opacity-100 transition">
                            <path
                              d="M5.625 15L24.375 15M24.375 15L15.9375 6.5625M24.375 15L15.9375 23.4375"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Profile;
